import { Link } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ClipboardCheck } from "lucide-react";
import {
  AdminPage,
  AdminPageHeader,
  AdminEmptyState,
  AdminErrorAlert,
  AdminLoadingSkeleton,
  AdminStatusBadge,
} from "@/components/admin-ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { artistHubEnabled, contentReviewQueue } from "@/services/firebase/artistHub";
import { REVIEW_TYPE_LABELS, type ReviewContentType, type ReviewItem } from "@/lib/artistAdmin";
import { isOverdue, waitedHours, waitedLabel } from "@/lib/moderation";

const PAGE_SIZE = 20;
const fmt = (ms: number | null | undefined) => (ms ? new Date(ms).toLocaleString("zh-TW", { hour12: false }) : "—");
const errorText = (e: unknown) => (e instanceof Error ? e.message : "操作未完成，請重試");
const TYPE_TONES: Record<ReviewContentType, "info" | "purple" | "warning"> = { post: "info", vlog: "purple", story: "warning" };

/**
 * O4 內容待審：跨藝人列出送審中（pending_review）的貼文／Vlog／限時動態. Staff only; sandbox (artistHubEnabled).
 *   contentReviewQueue {cursor, limit} → oldest first; rows past 24 h are highlighted.
 * Review itself happens in the artist's content workspace (/actors/:id/edit?tab=content).
 * Backend needs the Firestore composite index artistContent (status, updatedAt); while it builds the call fails and
 * the service surfaces artistAdminErrorMessage text (e.g.「後端索引尚未建立完成」).
 */
export default function ContentReviewPage() {
  const list = useInfiniteQuery({
    queryKey: ["content-review-queue"],
    queryFn: ({ pageParam }) => contentReviewQueue({ cursor: pageParam, limit: PAGE_SIZE }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: artistHubEnabled,
    retry: false,
    refetchInterval: 60000,
  });

  const header = (
    <AdminPageHeader
      title="內容待審"
      subtitle="所有藝人送審中的貼文、Vlog 與限時動態；審核請到該藝人的內容工作區進行，超過 24 小時會標紅"
      loading={list.isFetching}
      onRefresh={artistHubEnabled ? () => void list.refetch() : undefined}
    />
  );

  if (!artistHubEnabled) {
    return (
      <AdminPage>
        {header}
        <p className="text-sm text-muted-foreground">此環境尚未開放藝人專區（內容待審）。</p>
      </AdminPage>
    );
  }

  const now = Date.now();
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const overdue = items.filter((i) => isOverdue(i.submittedAt, now)).length;

  return (
    <AdminPage maxWidth="wide">
      {header}
      {list.isError && <AdminErrorAlert message={`無法載入待審內容：${errorText(list.error)}`} />}
      {list.isPending && <AdminLoadingSkeleton />}
      {list.isSuccess && items.length === 0 && (
        <AdminEmptyState
          icon={<ClipboardCheck className="h-5 w-5" />}
          title="目前沒有待審內容"
          description="藝人送審的內容會出現在這裡"
          variant="success"
        />
      )}
      {items.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>
              待審佇列
              <span className="ml-3 text-sm font-normal text-muted-foreground">
                已載入 {items.length} 件{list.hasNextPage ? "（還有更多）" : ""}
                {overdue > 0 && <span className="ml-2 font-semibold text-destructive">超過 24 小時 {overdue} 件</span>}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y">
              {items.map((item) => (
                <ReviewRow key={`${item.artistId}/${item.contentId}`} item={item} now={now} />
              ))}
            </ul>
            {list.hasNextPage && (
              <Button className="mt-4" variant="outline" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
                {list.isFetchingNextPage ? "載入中…" : "載入更多"}
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </AdminPage>
  );
}

function ReviewRow({ item, now }: { item: ReviewItem; now: number }) {
  const late = isOverdue(item.submittedAt, now);
  const hours = waitedHours(item.submittedAt, now);
  const fanOnly = item.audience !== "public";
  return (
    <li className={`flex flex-col gap-3 py-3 sm:flex-row sm:items-start sm:justify-between ${late ? "border-l-4 border-l-destructive pl-3" : ""}`}>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium text-foreground">{item.artistName || item.artistId}</span>
          <AdminStatusBadge label={REVIEW_TYPE_LABELS[item.type] ?? item.type} tone={TYPE_TONES[item.type] ?? "neutral"} />
          {fanOnly && <AdminStatusBadge label="鐵粉" tone="warning" />}
          {item.version > 1 && <span className="text-xs text-muted-foreground">第 {item.version} 版</span>}
        </div>
        <p className={`text-sm ${item.title ? "text-foreground" : "italic text-muted-foreground"}`}>{item.title || "未命名"}</p>
        {item.bodyPreview && <p className="line-clamp-2 break-words text-sm text-muted-foreground">{item.bodyPreview}</p>}
        <p className="text-xs text-muted-foreground">
          送審 {fmt(item.submittedAt)}・
          <span className={late ? "font-semibold text-destructive" : undefined}>
            {late ? "⚠ 已超過 24 小時・" : ""}等待 {waitedLabel(hours)}
          </span>
        </p>
      </div>
      <Button asChild size="sm" variant={late ? "default" : "outline"} className="shrink-0">
        <Link to={`/actors/${encodeURIComponent(item.artistId)}/edit?tab=content`}>前往審核</Link>
      </Button>
    </li>
  );
}
